/**
 * Jeopardy on a phone. Every control is `<PlayerInput>`, except the inert buzzer face
 * when locked: a lock has no request, and a phone that lost its big button reads as broken.
 */

import { useEffect, useState } from 'react';
import {
  PlayerInput,
  Scoreboard,
  Timer,
  type GameUiProps,
} from '@gsf/kit';
import { jeopardyView, type JeopardyView } from './view';

export function JeopardyPlayer({ snapshot, submit, events, connected, onLeave }: GameUiProps) {
  const view = jeopardyView(snapshot);
  const { you, shared, me } = view;
  const [showScores, setShowScores] = useState(false);

  // Escape closes the sheet on a desktop browser; tapping the backdrop is the
  // phone equivalent.
  useEffect(() => {
    if (!showScores) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setShowScores(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showScores]);

  // Show my own verdict for a moment, then fall back to the normal status.
  const judged = events.judged;
  const myId = you.participantId;
  const [verdictFlash, setVerdictFlash] = useState<{
    delta: number;
    ok: boolean;
    partial: boolean;
    bonus: number;
  } | null>(null);
  useEffect(() => {
    const payload = judged?.payload as
      | { participantId?: string; verdict?: string; delta?: number; partial?: boolean; bonus?: number }
      | undefined;
    if (!payload || !myId || payload.participantId !== myId) return;
    setVerdictFlash({
      delta: Number(payload.delta ?? 0),
      ok: payload.verdict === 'correct',
      partial: payload.partial === true,
      bonus: Number(payload.bonus ?? 0),
    });
    const t = setTimeout(() => setVerdictFlash(null), 3000);
    return () => clearTimeout(t);
  }, [judged, myId]);

  // Buzz the phone when a reply lands, and flash the banner briefly — a note
  // that just appears in place is easy to miss mid-round.
  const commentN = events.comment?.n;
  const [commentFlash, setCommentFlash] = useState(false);
  useEffect(() => {
    if (commentN === undefined) return;
    navigator.vibrate?.([40, 60, 40]);
    setCommentFlash(true);
    const t = setTimeout(() => setCommentFlash(false), 600);
    return () => clearTimeout(t);
  }, [commentN]);

  // `you` is core's and `private` is the game's; a snapshot always carries the first
  // and only carries the second once this device is a participant.
  if (!me || myId === null) {
    return (
      <div className="centered">
        <p className="muted">Waiting for the host…</p>
      </div>
    );
  }

  const score = view.scoreOf(myId);

  return (
    <div className="play">
      <div className="play-header">
        <span className="play-name">{you.name}</span>
        {!connected ? <span className="tag tag-bad">offline</span> : null}
        <span className={`play-score${score < 0 ? ' is-negative' : ''}`}>{score}</span>
      </div>

      {shared.sequential ? <QuestionProgress board={shared.board} missed={me.missedClueIds} /> : null}

      <div className="play-stage">
        {verdictFlash ? (
          <div
            className={`play-status ${
              verdictFlash.ok ? (verdictFlash.partial ? 'is-partial' : 'is-good') : 'is-bad'
            }`}
          >
            {verdictFlash.ok ? (verdictFlash.partial ? 'Partially correct' : 'Correct!') : 'Wrong'}{' '}
            {verdictFlash.delta > 0 ? '+' : ''}
            {verdictFlash.delta}
            {verdictFlash.bonus > 0 ? <span className="play-speed"> +{verdictFlash.bonus} speed</span> : null}
          </div>
        ) : null}

        {/* The host's private reply, above whatever control is showing. */}
        {me.comment ? (
          <div className={`play-comment${commentFlash ? ' is-new' : ''}`}>
            <span className="play-comment-label">From the host</span>
            {me.comment}
          </div>
        ) : null}

        <PlayerStage view={view} submit={submit} />
      </div>

      <div className="play-footer">
        <button className="btn btn-sm" onClick={() => setShowScores(true)}>
          Scores ({view.standings.length})
        </button>
        <span className="spacer" />
        {onLeave ? (
          <button className="btn btn-sm btn-ghost" onClick={onLeave} title="Forget me and rejoin">
            leave
          </button>
        ) : null}
      </div>

      {/* Scores live in a sheet rather than on the main screen: a phone-width
          list of players can't be shown inline without pushing the clue and the
          buzzer sideways. */}
      {showScores ? (
        <div className="sheet-backdrop" onClick={() => setShowScores(false)}>
          <div
            className="sheet"
            role="dialog"
            aria-label="Scores"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet-head">
              <h2 className="panel-title" style={{ margin: 0 }}>
                Scores
              </h2>
              <span className="spacer" />
              <button className="btn btn-sm" onClick={() => setShowScores(false)}>
                Close
              </button>
            </div>
            <Scoreboard
              players={view.standings}
              activeParticipantId={shared.activeParticipantId}
              meId={myId}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A sequential game's run of questions as a row of ingots: struck gold (got it), cracked (missed),
 * glowing (in play) and cold iron (still to come). Pure CSS, so it needs no art.
 */
function QuestionProgress({
  board,
  missed,
}: {
  board: JeopardyView['shared']['board'];
  missed: readonly string[];
}) {
  const cells = board.flatMap((column) => column.cells).filter((cell) => !cell.draft);
  const done = cells.filter((cell) => cell.state === 'done').length;
  const misses = cells.filter((cell) => missed.includes(cell.clueId)).length;
  const current = cells.some((cell) => cell.state === 'in_play') ? done + 1 : done;
  return (
    <div className="forge-progress">
      <span className="forge-progress-label">
        Question {Math.max(1, current)} of {cells.length}
      </span>
      <div
        className="forge-progress-bar"
        role="progressbar"
        aria-label="Questions completed"
        aria-valuemin={0}
        aria-valuemax={cells.length}
        aria-valuenow={done}
        aria-valuetext={`${done} done${misses > 0 ? `, ${misses} missed` : ''}, ${cells.length - done} to go`}
      >
        {cells.map((cell) => (
          <span
            key={cell.clueId}
            className={`forge-ingot is-${cell.state}${missed.includes(cell.clueId) ? ' is-missed' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The board on a phone (hostless Play Now): categories down the screen, a row of point tiles each.
 * Spent tiles are blanked; the current picker's tiles are live buttons, everyone else sees it
 * read-only so they can follow along.
 */
function PhoneBoard({
  board,
  canPick,
  onPick,
}: {
  board: JeopardyView['shared']['board'];
  canPick: boolean;
  onPick: (clueId: string) => void;
}) {
  // The classic grid: a category heading per column across the top, values rising down each
  // column. Cells come value-ascending, so tier 0 is the top row.
  const tiers = Math.max(0, ...board.map((c) => c.cells.length));
  return (
    <div
      className="pn-board"
      style={{ gridTemplateColumns: `repeat(${Math.max(1, board.length)}, 1fr)` }}
    >
      {board.map((column) => (
        <div key={column.categoryId} className="pn-cat">
          {column.name || '—'}
        </div>
      ))}
      {Array.from({ length: tiers }, (_unused, tier) =>
        board.map((column) => {
          const cell = column.cells[tier];
          if (!cell) {
            return (
              <div key={`${column.categoryId}-${tier}`} className="pn-tile is-empty" aria-hidden="true">
                —
              </div>
            );
          }
          const open = cell.state === 'available' && !cell.draft;
          return (
            <button
              key={cell.clueId}
              type="button"
              className={`pn-tile${open ? '' : ' is-spent'}`}
              disabled={!canPick || !open}
              onClick={() => open && onPick(cell.clueId)}
            >
              {open ? (cell.label ?? cell.points) : ''}
            </button>
          );
        }),
      )}
    </div>
  );
}

/** The one control this player should be looking at right now. */
function PlayerStage({
  view,
  submit,
}: {
  view: JeopardyView;
  submit: GameUiProps['submit'];
}) {
  const { shared, you } = view;
  const me = view.me!;
  const clue = shared.current;

  // The form exists because the server asked this phone for a bet; `shared` only
  // frames whose daily double it is.
  const bet = view.ask('wager');
  if (me.isWagering && bet) {
    // The daily-double claimant and, in the final round, every player — core only opens
    // a `wager` request for a phone that is actually betting, so one branch serves both.
    return <PlayerInput request={bet} onSubmit={submit} />;
  }

  if (shared.phase === 'wager') {
    return (
      <p className="play-hint">
        Wager — {view.nameOf(shared.wager?.participantId ?? null) ?? 'someone'} is placing a
        wager.
      </p>
    );
  }

  // Final round: this phone has placed its bet (the request is gone) and is waiting for
  // the host to reveal the clue.
  if (shared.phase === 'final_wager') {
    return (
      <p className="play-hint">
        {me.wagerAmount === null
          ? 'Final round — waiting for a wager.'
          : `Wager locked in: ${me.wagerAmount}. Waiting for the clue…`}
      </p>
    );
  }

  // The clue's text is the request's `prompt`, so `<PlayerInput>` prints it. A collect
  // clue opens one of these — typed text or a choice list — never both at once.
  const answering = view.ask('text') ?? view.ask('number') ?? view.ask('choice');
  if (me.canAnswer && clue && answering) {
    return (
      <>
        {/* Core's countdown, not the request's: Jeopardy pairs an undeadlined collect
            request with a `timer` effect. An untimed clue shows no clock. */}
        <Timer
          endsAt={view.core.deadlineAt}
          serverNow={view.core.serverNow}
          variant="phone"
          decimals={2}
        />
        <PlayerInput request={answering} onSubmit={submit} />
      </>
    );
  }

  if (shared.phase === 'collecting') {
    return (
      <p className="play-hint">
        {shared.hostless ? 'Answer’s in. Waiting for everyone else…' : 'Answers are in. Waiting for the host…'}
      </p>
    );
  }

  if (shared.phase === 'grading') {
    return (
      <>
        <p className="play-hint">
          {shared.hostless ? 'Marking the answers…' : 'Host is marking the answers.'}
        </p>
        {me.submittedAnswer ? <p className="play-clue">You said: “{me.submittedAnswer}”</p> : null}
      </>
    );
  }

  // A resolved choice clue: the options again, the right one green, this player's wrong
  // pick red, with a word on how they did.
  if (shared.phase === 'resolved' && clue && me.choiceResult) {
    const { verdict, options } = me.choiceResult;
    // A flawless pick is every correct option taken and no wrong one; scoring `correct`
    // without that is partial credit (a partial-credit multiple answer).
    const flawless = options.every((option) => option.correct === option.picked);
    const partial = verdict === 'correct' && !flawless;
    const statusClass = verdict !== 'correct' ? 'is-bad' : partial ? 'is-partial' : 'is-good';
    const statusText =
      verdict !== 'correct' ? 'You got it wrong' : partial ? 'Partially correct' : 'You got it right!';
    return (
      <>
        {view.prompt ? <p className="play-clue">{view.prompt}</p> : null}
        {verdict ? <div className={`play-status ${statusClass}`}>{statusText}</div> : null}
        <div className="col choice-options">
          {options.map((option) => {
            // A correct option this player skipped counts against them (it matters for
            // multiple answer); a wrong pick is their mistake; a right pick is a hit.
            const status = option.correct
              ? option.picked
                ? 'hit'
                : 'missed'
              : option.picked
                ? 'wrong'
                : 'none';
            const mark =
              status === 'hit' ? 'Correct' : status === 'missed' ? 'Missed' : status === 'wrong' ? 'Wrong' : null;
            return (
              <div
                key={option.id}
                className={`choice-option choice-result${
                  status === 'hit'
                    ? ' is-correct'
                    : status === 'missed'
                      ? ' is-missed'
                      : status === 'wrong'
                        ? ' is-wrong'
                        : ''
                }`}
              >
                <span>{option.label}</span>
                {mark ? <span className="choice-mark">{mark}</span> : null}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  if (shared.phase === 'resolved' && clue) {
    return (
      <>
        <p className="play-hint">The answer was</p>
        <p className="play-clue" style={{ fontSize: '1.3rem', fontWeight: 700 }}>
          {view.clueAnswer}
        </p>
      </>
    );
  }

  if (shared.phase === 'board' || !clue) {
    // Hostless: the board lives on every phone, and the current picker taps a tile. The pick is
    // the only `choice` request open while on the board.
    if (shared.hostless) {
      const pick = view.ask('choice');
      const pickerName = view.nameOf(shared.currentPickerId);
      return (
        <>
          {pick ? (
            <div className="play-status is-good">Your turn — tap a clue</div>
          ) : (
            <p className="play-hint">
              {pickerName ? `${pickerName} is choosing a clue…` : 'Choosing a clue…'}
            </p>
          )}
          <PhoneBoard
            board={shared.board}
            canPick={pick !== null}
            onPick={(clueId) => pick && submit(pick.id, clueId)}
          />
        </>
      );
    }
    return <p className="play-hint">Look at the TV — the host is picking a clue.</p>;
  }

  if (shared.phase === 'selected') {
    return <p className="play-hint">Get ready…</p>;
  }

  // A daily double belongs to whoever picked it, and a collect clue has no buzzer; only a
  // buzzer-played clue (verbal, or auto-gradable in buzzer mode) shows the big button.
  if (!clue.onBuzzer) {
    return <p className="play-hint">{shared.hostless ? 'Answer on your phone.' : 'Watch the TV.'}</p>;
  }

  const press = view.ask('press');
  const myBuzzOrder = shared.buzzes.find((b) => b.participantId === you.participantId)?.order;

  // What the button says when it is not a live buzzer; null means there is something
  // to press.
  const face = me.lockedOut
    ? 'OUT'
    : me.holdsBuzz
      ? 'YOU!'
      : me.hasBuzzed
        ? `#${myBuzzOrder ?? '?'}`
        : null;

  let status: string | null = null;
  let statusClass = 'is-wait';

  if (me.lockedOut) {
    status = 'You missed this one';
    statusClass = 'is-bad';
  } else if (me.holdsBuzz) {
    status = 'Answer out loud!';
    statusClass = 'is-good';
  } else if (me.hasBuzzed) {
    status = `You're #${myBuzzOrder ?? '?'} in line`;
  } else if (shared.phase === 'answering') {
    status = 'Someone else buzzed — tap to queue up';
  } else if (shared.phase === 'revealed') {
    status = 'Buzzers locked';
  }

  return (
    <>
      {/* No phase check needed: `viewShared` redacts the prompt until the reveal. */}
      {view.prompt ? <p className="play-clue">{view.prompt}</p> : null}

      {press && face === null ? (
        <PlayerInput request={press} onSubmit={submit} />
      ) : (
        <button className="buzzer" type="button" disabled>
          {face ?? 'BUZZ'}
        </button>
      )}
      {status ? <div className={`play-status ${statusClass}`}>{status}</div> : null}
    </>
  );
}
